import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import React from 'react';
import tw from 'tailwind.macro';
import Button from '../components/button';
import Layout from '../components/layout';
import LeadText from '../components/leadText';
import Section from '../components/section';
import SEO from '../components/seo';
import PageNavigator from '../components/pageNavigator';
import SafeLink from '../components/safeLink';
import Grid from '../components/grid';

const Summit2025 = ({ data, location }) => {
  const parentTitle = 'The Centre for Social Justice presents:';
  const pageTitle = 'The 7th Social Justice Summit';

  return (
    <Layout location={location} parentTitle={parentTitle} pageTitle={pageTitle}>
      <SEO title={pageTitle} image={data.hero.childImageSharp.fluid.src} />
      <div css={[tw`relative flex items-center py-10`, `min-height:30vh;`]}>
        <div css={tw`absolute inset-0 z-0`}>
          <video
            autoPlay
            muted
            loop
            playsInline
            css={tw`w-full h-full object-cover`}
          >
            <source src="/adobestock_894254768-2.webm" type="video/webm" />
            <source src="/adobestock_894254768-2.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="relative container">
          <div>
            <LeadText
              css={[
                tw`my-0 uppercase tracking-wide text-white`,
                `text-shadow:0 0 10px rgba(0,0,0,0.5)`,
              ]}
              className="text-shadow"
            >
              {parentTitle}
            </LeadText>
            <h1
              className="text-shadow"
              css={[
                tw`my-0 text-white tracking-wider`,
                `text-shadow:0 0 10px rgba(0,0,0,0.5)`,
              ]}
            >
              {pageTitle}
            </h1>
          </div>
        </div>
      </div>
      <PageNavigator
        items={{
          welcome: 'Welcome',
          concept: 'Concept',
          programme: 'Programme',
          previous: 'Previous Summits',
          contact: 'Contact',
        }}
        buttonText="Watch keynote"
        buttonLink="https://www.youtube.com/live/LOr6Og_epIo"
      />
      <Section id="welcome">
        <Img
          fluid={data.summit2025Banner.childImageSharp.fluid}
          alt="The 7th International Social Justice Summit, 17 October 2025"
          css={tw`mb-10 lg:mb-20 w-full`}
        />
        <Grid lgGridCols={2} lgGap={5}>
          <div>
            <h2>Welcome!</h2>
            <h3>
              The theme of the summit is{' '}
              <em>
                Social Justice, Food Security and Peace in a Turbulent World:
                Pathways to Equality, Solidarity, Sustainability and Climate
                Resilience.
              </em>
            </h3>
            <p>
              <strong>Hosted at</strong> the Cape Town International Convention
              Centre (CTICC), Cape Town, South Africa &middot; 17 October 2025
            </p>
            <p>
              <strong>Presented by</strong> the Centre for Social Justice,
              Stellenbosch University and partners including the United Nations
              and the Department of Planning, Monitoring and Evaluation in the
              Presidency
            </p>
            <Button
              to="https://www.youtube.com/live/LOr6Og_epIo"
              size="md"
            >
              Watch keynote
            </Button>
          </div>
          <iframe width="560" height="315" src="https://www.youtube.com/embed/LOr6Og_epIo?si=IkjsAFaLEAXOSP-h" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
        </Grid>
      </Section>
      <Section id="concept" isShaded>
        <h2>Concept</h2>
        <LeadText>
          <blockquote>
            “In an interconnected world, the challenges faced by one nation
            affect all nations. Through solidarity we seek to advance a unified
            effort and mutual support among member nations. By advancing
            equality we seek to ensure fair treatment, opportunities and
            advancement for all individuals and nations. This must be
            irrespective of their economic status, gender, race, geographic
            location or other characteristics. Sustainable development is about
            meeting the needs of the present without compromising the ability of
            future generations to meet their needs.” – President Cyril Ramaphosa
          </blockquote>
        </LeadText>
        <p>
          No Nation can be free if it cannot feed itself, fuel itself and defend
          itself, yet no nation is an island hence the importance of global
          solidarity in the advancement of social justice. The Social Justice
          Summit (SJS), in its 7<sup>th</sup> iteration, hosted by the Centre
          for Social Justice (CSJ) at Stellenbosch University will convene
          policymakers, legislators, political parties, judiciary, traditional
          and religious leaders, civil society and academia to foster high-level
          dialogue on food security as a cornerstone for social justice,
          equality and sustainable development. Scheduled for Friday, 17 October
          2025 and under the theme "Social Justice, Food Security and Peace:
          Pathways to Equality, Solidarity, Sustainability and Climate
          Resilience", the Summit leverages the country's G20 leadership to
          champion the African agenda and global cooperation. The Summit will
          take place at the Cape Town International Convention Centre (CTICC).
        </p>
        <p>
          South Africa's G20 Presidency commenced on 1 December 2024 and builds
          on the legacies of Indonesia, India and Brazil, which prioritised
          sustainable development and poverty alleviation. The Summit aligns
          with the G20 Development Working Group (DWG) priorities, including
          mobilising finance for development, promoting social protection floors
          and addressing global public goods (GPGs) to enhance resilience. By
          integrating these priorities, the Summit aims to accelerate progress
          toward Sustainable Development Goal 2 (Zero Hunger) and the African
          Union's Agenda 2063.
        </p>
        <p>
          As a platform for intersectoral collaboration, the Summit will bridge
          constitutional commitments and policy outcomes, positioning food
          justice as a transformative driver of social cohesion, regional food
          security and global solidarity. This aligns with South Africa's
          National Development Plan (NDP) 2030, which similarly prioritises the
          elimination of poverty and reduction of inequality, with explicit
          commitments under Chapter 11 (Social Protection) and Chapter 6
          (Inclusive Rural Economy) to address hunger and improve food systems.
        </p>
        <Button
          to="/downloads/events/2025-10-summit-concept-note.pdf"
          size="md"
          css={tw`mb-2 mr-2 bg-dark-gray`}
        >
          Download concept note
        </Button>
      </Section>
      {/* <Section id="registration" isShaded>
        <h2>Registration</h2>
        <p>You can now buy tickets to attend the conference.</p>
        <Button
          to="https://www.quicket.co.za/events/269646-5th-international-social-justice-conference-and-6th-annual-social-justice-summit#/"
          size="lg"
          css={tw`mb-2 mr-2`}
        >
          Buy tickets
        </Button>
      </Section> */}
      {/* <Section id="resources" isShaded>
        <h2>Presenter resources</h2>
        <p>The call for abstracts is now closed. Please note the following important dates:</p>
        <ul>
          <li>Deadline for submission of PowerPoint Presentations to fanele@sun.ac.za: 09 October 2024</li>
          <li>Deadline for submission of the draft paper: 11 October 2024</li>
          <li>Deadline for submission of the final paper: 15 November 2024</li>
          <li>Deadline for review and approval of the final paper by the Organising Committee: 30 November 2024</li>
        </ul>
        <br/>
        <Button
          to="/downloads/events/2024-07-SocialJustice_InternationalConference&Summit_Paper&PresentationGuidelines_03July2024versionF.pdf"
          size="md"
          css={tw`mb-2 mr-2`}
        >
          Download guidelines
        </Button> <Button
          to="/downloads/events/conference-summit-2024-poster-tips-20240724.pdf"
          size="md"
          css={tw`mb-2 mr-2 bg-dark-gray`}
        >
          Download poster tips
        </Button> <Button
          to="/downloads/events/conference-summit-2024-ppt-template.pptx"
          size="md"
          css={tw`mb-2 mr-2 bg-dark-gray`}
        >
          Download presentation template
        </Button>  
      </Section>      
      <Section id="statements" isShaded>
        <h2>Summit and conference outputs</h2>        
        <ul>          
        <li><SafeLink to="/downloads/events/2024-10-cape-town-declaration.pdf" css={tw`border-b-2 border-maroon hover:text-maroon`}>Download the Cape Town Declaration on The Role of Business and Civil Society in Advancing
Social Justice</SafeLink>, adopted at the 4th International Conference on Social Justice on 11 October 2024</li>
          <li><SafeLink to="/downloads/events/2024-10-cape-town-statement.pdf" css={tw`border-b-2 border-maroon hover:text-maroon`}>Download the Cape Town Statement on The Role of Business and Civil Society in Advancing
Social Justice</SafeLink>, adopted at the 5th Social Justice Summit on 12 October 2024</li>
        </ul>
      </Section> */}
      <Section id="programme">
        <h2>Programme</h2>
        <LeadText>Download the final summit programme below.</LeadText>
        <Button
          to="/downloads/events/2025-10-summit-programme.pdf"
          size="md"
          css={tw`mb-2 mr-2`}
        >
          Download programme
        </Button>
      </Section>
      <Section id="previous" isShaded>
        <h2>Previous Summits</h2>
        <LeadText>
          You can watch free recordings of previous summits on our website.
          <ul>
            <li>
              <SafeLink
                to="/summit-2020/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2020 summit &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/summit-conference-2021/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2021 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/summit-conference-2022/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2022 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/conference-summit-2023/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2023 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/summit-2024/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2024 summit &rarr;
              </SafeLink>
            </li>
          </ul>
        </LeadText>
      </Section>
      <Section id="contact">
        <h2>Contact</h2>
        <LeadText>
          Please contact the summit project manager, Thembalethu Seyisi, with
          any queries. You can reach him at{' '}
          <SafeLink
            to="mailto:tseyisi@sun.ac.za"
            css={tw`border-b-2 border-maroon hover:text-maroon`}
          >
            tseyisi@sun.ac.za
          </SafeLink>{' '}
          or via the contact form below.
        </LeadText>
        <form action="https://usebasin.com/f/44697ef058ef" method="POST">
          <label className="block mb-5">
            <span className="text-dark">Name:</span>
            <input
              className="form-input mt-1 block w-full"
              name="name"
              placeholder="John Smith"
            />
          </label>
          <label className="block mb-5">
            <span className="text-dark">Email:</span>
            <input
              className="form-input mt-1 block w-full"
              name="email"
              placeholder="john@smith.com"
            />
          </label>
          <label htmlFor="phone" css={tw`mb-5`}>
            <span css={tw`block`}>Phone:</span>
            <input
              name="phone"
              type="text"
              placeholder="+27 72 123 4567"
              className="form-input mt-1 block w-full"
            />
          </label>
          <label className="block mb-5">
            <span className="text-dark">Message:</span>
            <textarea
              className="form-textarea mt-1 block w-full"
              rows="3"
              name="message"
              placeholder="Your message"
            />
          </label>
          {/* <label class="flex items-center mb-5">
                  <input
                    type="checkbox"
                    name="privacy-policy-agreed"
                    class="form-checkbox"
                  />
                  <span class="ml-2">
                    I agree to the{" "}
                    <SafeLink
                      to="/privacy-policy/"
                      css={tw`border-b-2 border-primary hover:text-primary`}
                      forceBlank
                    >
                      privacy policy
                    </SafeLink>
                  </span>
  </label> */}
          <Button type="submit" size="md">
            Submit &rarr;
          </Button>
        </form>
      </Section>
    </Layout>
  );
};

export default Summit2025;

export const pageQuery = graphql`
  query {
    hero: file(relativePath: { eq: "2025-08-summit-banner.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    summit2025Banner: file(relativePath: { eq: "2025-08-summit-banner.png" }) {
      childImageSharp {
        fluid(quality: 100) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`;
